import { LocaleCodesSlugPrefix } from '../../src/constants/localization.constants'
import {
  getBlogFeaturedResource,
  setBlogHomePageFeaturedSets,
  setListingBuckets,
} from '../../src/createPages/blog/createBlogPages.utils'
import { mapLocale } from '../../src/utils/pageCreation.utils'
import cache from '../cache.utils'
import formatComponentBlogFeaturedResourceData from '../component/blogFeaturedResource'
import formatComponentSubscribeData from '../component/subscribe'
import MODULE_MAPPING from '../constants/newModuleMapping'
import formatNavMenu from '../menu/menuNav'
import { allBlogData } from '../page/blogPost'
import formatMainBlogPageData from '../page/mainBlog'
import { getAll, pageLocale } from '../preview.utils'

export default async (slug: string, client: any) => {
  try {
    const { name, code } = pageLocale(slug)
    let urlParts = slug.split('/')
    let pageType = ''
    const isSlugLocalized = code !== 'en-US'

    if (slug.match(/^((de|es|ja|ko|pt|es|fr|cn)\/)?blog(\/\d+)?$/))
      pageType = MODULE_MAPPING.PAGE_MAIN_BLOG
    else if (slug.match(/^((de|es|ja|ko|pt|es|fr|cn)\/)?blog\/[^\/]*(\/\d+)?$/))
      pageType = 'PAGE_SUB_LISTING'
    else if (
      (!isSlugLocalized && urlParts.length === 3) ||
      (isSlugLocalized && urlParts.length === 4)
    )
      pageType = MODULE_MAPPING.PAGE_BLOG_POST

    // get all blog posts
    let allBlogPosts = await getAll(client, MODULE_MAPPING.PAGE_BLOG_POST, {
      locale: code,
      'fields.entryLocale': name,
      order: '-fields.date',
    })

    // format blog posts
    allBlogPosts = allBlogPosts.map((blogPost: any) => {
      return { node: allBlogData(blogPost) }
    })

    // get all blog featured resource
    let allBlogFeaturedResource = await getAll(client, MODULE_MAPPING.BLOG_FEATURED_RESOURCE, {
      locale: code,
      order: 'fields.publishDate',
    })

    // format blog featured resource
    allBlogFeaturedResource = allBlogFeaturedResource.map((blogFeaturedResource: any) =>
      formatComponentBlogFeaturedResourceData(blogFeaturedResource)
    )

    // get main blog subscribe
    let mainBlogSubscribe
    if (Object.keys(cache.get('subscribe').length === 0)) {
      mainBlogSubscribe = await client.getEntries({
        content_type: MODULE_MAPPING.SUBSCRIBE,
        'fields.useCase': 'Main Blog',
        locale: code,
        include: 10,
      })
      mainBlogSubscribe = formatComponentSubscribeData(mainBlogSubscribe?.items?.[0])
      cache.set('subscribe', mainBlogSubscribe)
    } else {
      mainBlogSubscribe = cache.get('subscribe')
    }

    // get blog nav menu
    let blogNavMenu
    if (Object.keys(cache.get('blogNavMenu')).length === 0) {
      blogNavMenu = await client.getEntries({
        content_type: MODULE_MAPPING.NAV_MENU,
        'fields.location': 'Main Blog',
        locale: code,
        include: 10,
      })
      blogNavMenu = formatNavMenu(blogNavMenu?.items?.[0])
      cache.set('blogNavMenu', blogNavMenu)
    } else {
      blogNavMenu = cache.get('blogNavMenu')
    }
    if (pageType === MODULE_MAPPING.PAGE_BLOG_POST) {
      if (isSlugLocalized) {
        return blogPostPage({
          slug: `${LocaleCodesSlugPrefix[code]}/${urlParts[3]}`,
          client,
          allBlogPosts,
          allBlogFeaturedResource,
          mainBlogSubscribe,
          blogNavMenu,
          url: slug,
        })
      }

      return blogPostPage({
        slug: urlParts[2],
        client,
        allBlogPosts,
        allBlogFeaturedResource,
        mainBlogSubscribe,
        blogNavMenu,
        url: slug,
      })
    } else if (pageType === MODULE_MAPPING.PAGE_MAIN_BLOG)
      return mainBlogPage(
        slug,
        client,
        allBlogPosts,
        allBlogFeaturedResource,
        mainBlogSubscribe,
        blogNavMenu
      )
    else {
      const { authorListing, categoryListing, collectionListing }: any = setListingBuckets({
        blogPosts: allBlogPosts,
        isPreview: true,
      })
      let urlParts = slug.split('/')
      let id = urlParts[1]

      if (isSlugLocalized) {
        id = urlParts[2]
      }

      if (authorListing.has(id)) {
        return subListingPages(
          slug,
          authorListing.get(id)[name],
          allBlogFeaturedResource,
          mainBlogSubscribe,
          blogNavMenu,
          MODULE_MAPPING.PAGE_BLOG_AUTHOR_LISTING
        )
      } else if (categoryListing.has(id)) {
        return subListingPages(
          slug,
          categoryListing.get(id)[name],
          allBlogFeaturedResource,
          mainBlogSubscribe,
          blogNavMenu,
          MODULE_MAPPING.PAGE_BLOG_CATEGORY_LISTING
        )
      } else if (collectionListing.has(id)) {
        return subListingPages(
          slug,
          collectionListing.get(id)[name],
          allBlogFeaturedResource,
          mainBlogSubscribe,
          blogNavMenu,
          MODULE_MAPPING.PAGE_BLOG_COLLECTION_LISTING
        )
      } else {
        return {}
      }
    }
  } catch (err) {
    console.log(err)
    return {
      error: err,
    }
  }
}

/**
 * Blog Detail Page
 * @param slug
 * @param client
 * @param allBlogPosts
 * @param allBlogFeaturedResource
 * @param mainBlogSubscribe
 * @param blogNavMenu
 * @returns
 */
const blogPostPage = async ({
  slug,
  client,
  allBlogPosts,
  allBlogFeaturedResource,
  mainBlogSubscribe,
  blogNavMenu,
  url,
}: {
  slug: string
  client: any
  allBlogPosts: any
  allBlogFeaturedResource: any
  mainBlogSubscribe: any
  blogNavMenu: any
  url: string
}) => {
  const { name, code } = pageLocale(slug)

  let pageEntry = await client.getEntries({
    content_type: MODULE_MAPPING.PAGE_BLOG_POST,
    locale: code,
    'fields.entryLocale': name,
    'fields.slug': slug,
    include: 10,
  })

  if (pageEntry.total === 0) {
    return {}
  }

  // remove circular references
  pageEntry = JSON.parse(pageEntry.stringifySafe())

  let content: any = pageEntry?.items?.[0] ? allBlogData(pageEntry.items[0]) : {}

  // get list of blogs tags for this article to find corresponding blog featured resource
  let tags = content.blogTags?.length ? content.blogTags.map((tag: any) => tag.name) : []

  let blogFeaturedResource = getBlogFeaturedResource(allBlogFeaturedResource, tags) || []

  // get related articles from category
  let relatedArticles = []
  const { categoryToBlog }: any = setListingBuckets({ blogPosts: allBlogPosts })

  if (
    content.blogCategory &&
    content.blogCategory.name &&
    categoryToBlog[content.blogCategory.name] &&
    content.entryLocale
  ) {
    let categoryId = content.blogCategory.name
    relatedArticles = categoryToBlog[categoryId][content.entryLocale]
  }

  return {
    contentType: MODULE_MAPPING.PAGE_BLOG_POST,
    pageData: {
      location: {
        pathname: `/${url}`,
        origin: 'http://localhost:8000',
      },
      path: url,
      pageContext: {
        pageData: {
          path: '/' + url,
        },
        blogPostData: {
          ...content,
          blogFeaturedResources: [blogFeaturedResource],
        },
        relatedArticles,
        subscribe: mainBlogSubscribe,
        blogNav: blogNavMenu,
        blogFeaturedResource: [blogFeaturedResource],
        pageLocale: code,
      },
    },
  }
}

const mainBlogPage = async (
  slug: string,
  client: any,
  allBlogPosts: any,
  allBlogFeaturedResource: any,
  mainBlogSubscribe: any,
  blogNavMenu: any
) => {
  const { name, code } = pageLocale(slug)

  let baseSlug = slug
  let urlParts = slug.split('/')

  const isSlugLocalized = code !== 'en-US'
  if (isSlugLocalized) {
    baseSlug = urlParts[0] + '/' + urlParts[1]
  } else {
    baseSlug = urlParts[0]
  }

  let pageEntry = await client.getEntries({
    content_type: MODULE_MAPPING.PAGE_MAIN_BLOG,
    locale: code,
    'fields.entryLocale': name,
    'fields.slug': baseSlug,
    include: 10,
  })
  if (pageEntry.total === 0) {
    return {}
  }

  // remove circular references
  pageEntry = JSON.parse(pageEntry.stringifySafe())

  let content: any = pageEntry?.items?.[0] ? formatMainBlogPageData(pageEntry.items[0]) : {}

  let blogCollectionRow = content.collectionRow

  let pageNumberIdx = code !== 'en-US' ? 2 : 1
  let pageNum = parseInt(urlParts[pageNumberIdx])
  let index = !isNaN(pageNum) ? pageNum : 1
  let blogFeaturedResource = getBlogFeaturedResource(allBlogFeaturedResource, ['Blog Home Tag'])

  const localizedBlogPost: any = {}
  for (let nodePost of allBlogPosts) {
    const post = nodePost.node
    const localeInfo = mapLocale(post.entryLocale)
    let postLocale = ''
    if (localeInfo) {
      if (localeInfo.pageLocale) postLocale = localeInfo.pageLocale
    }
    if (post.node_locale === postLocale) {
      const locale = post.entryLocale
      if (!localizedBlogPost[locale]) {
        localizedBlogPost[locale] = []
      }
      localizedBlogPost[locale].push(nodePost)
    }
  }
  const { primaryPost, secondaryPosts, excludeBlogFeatured } = setBlogHomePageFeaturedSets(
    localizedBlogPost[name]
  )

  // If blog featured resource is set, total listings per page should be 13 to accommadate for the featured resource slot
  let blogTeasersPerPage = blogFeaturedResource ? 13 : 14

  const blogPostsWithoutFeatured =
    (localizedBlogPost[name] &&
      localizedBlogPost[name].filter(
        (_: any, idx: any) => excludeBlogFeatured.indexOf(idx) === -1
      )) ||
    []

  let postGroupLength = blogPostsWithoutFeatured.length

  let totalBlogLandingPages = Math.ceil(postGroupLength / blogTeasersPerPage)

  let offSet = index === 1 ? 0 : (index - 1) * blogTeasersPerPage

  return {
    contentType: MODULE_MAPPING.PAGE_MAIN_BLOG,
    allBlogPosts: localizedBlogPost[name],
    pageData: {
      location: {
        pathname: `/${slug}`,
        origin: 'http://localhost:8000',
      },
      path: slug,
      pageContext: {
        pageData: {
          path: slug,
          totalPages: postGroupLength,
          pageNumber: index,
          totalBlogLandingPages,
          blogPosts: blogPostsWithoutFeatured.slice(offSet, offSet + blogTeasersPerPage),

          subscribe: mainBlogSubscribe,
        },
        homePageData: content,
        primaryPost,
        secondaryPosts,
        blogNav: blogNavMenu,
        collectionRowData: blogCollectionRow,
        blogFeaturedResource,
        pageLocale: code,
      },
    },
  }
}

const subListingPages = (
  slug: string,
  listings: any,
  allBlogFeaturedResource: any,
  mainBlogSubscribe: any,
  blogNavMenu: any,
  type: string
) => {
  let blogFeaturedResource = getBlogFeaturedResource(allBlogFeaturedResource, ['Blog Home Tag'])

  // If blog featured resource is set, total listings per page should be 13 to accommadate for the featured resource slot
  let blogTeasersPerPage = blogFeaturedResource ? 13 : 14

  let urlParts = slug.split('/')
  let collectionName = urlParts[1]

  let pageNum = parseInt(urlParts[2])
  let index = !isNaN(pageNum) ? pageNum : 1

  let totalListingPages = Math.ceil(listings.length / blogTeasersPerPage)

  let seoMetadata = null
  let categoryDescription = ''
  let collectionDescription = ''
  let collectionNameUnformatted = ''
  let authorBio = listings[0].author ? listings[0].author : null
  switch (type) {
    case MODULE_MAPPING.PAGE_BLOG_COLLECTION_LISTING:
      if (listings[0].blogTags) {
        let matchingTag = listings[0].blogTags.find((tag: any) => {
          let id = tag.name
            .toLowerCase()
            .replace(/ *\([^)]*\) */g, '')
            .replace(/\s/g, '-')
          if (tag.name === 'Digital Transformation') {
            id = 'digital-business-transformation'
          } else if (tag.name === 'Automation') {
            id = 'enterprise-automation'
          } else if (tag.name === 'Finance & Accounting (F&A)') {
            id = 'finance-and-accounting'
          }
          if (id === collectionName) {
            collectionNameUnformatted = tag.name
            return true
          }
          return false
        })
        seoMetadata = matchingTag ? matchingTag.seoMetadata : null
        collectionDescription =
          matchingTag && matchingTag.description ? matchingTag.description : null
      }
      break
    case MODULE_MAPPING.PAGE_BLOG_CATEGORY_LISTING:
      collectionNameUnformatted = listings?.[0]?.blogCategory?.name
      seoMetadata =
        listings[0].blogCategory && listings[0].blogCategory.seoMetadata
          ? listings[0].blogCategory.seoMetadata
          : null
      categoryDescription =
        listings[0].blogCategory && listings[0].blogCategory.description
          ? listings[0].blogCategory.description
          : null
      break
    case MODULE_MAPPING.PAGE_BLOG_AUTHOR_LISTING:
      seoMetadata =
        listings[0].author && listings[0].author.seoMetadata ? listings[0].author.seoMetadata : null
      break
    default:
      break
  }
  if (index !== 1 && seoMetadata && seoMetadata.pageCanonicalUrl) {
    seoMetadata.pageCanonicalUrl += `/${index}`
  }

  let offSet = index === 1 ? 0 : (index - 1) * blogTeasersPerPage

  return {
    contentType: type,
    pageData: {
      location: {
        pathname: `/${slug}`,
        origin: 'http://localhost:8000',
      },
      path: slug,
      pageContext: {
        pageData: {
          path: slug,
          bio: authorBio,
          collectionName: collectionNameUnformatted,
          categoryDescription: categoryDescription,
          collectionDescription: collectionDescription,
          slug: `/${urlParts[0]}/${urlParts[1]}`,
          totalPages: listings.length,
          pageNumber: index,
          blogs: listings.slice(offSet, offSet + blogTeasersPerPage),
          seoMetadata: seoMetadata,
          subscribe: mainBlogSubscribe,
          blogNav: blogNavMenu,
          totalListingPages,
        },
        blogFeaturedResource,
      },
    },
  }
}
